import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { BarChart3, BookOpen, Euro, MessageCircle, Star, Users } from "lucide-react";

const getStoredToken = () => {
  if (typeof window === "undefined") return "";
  return (
    window.localStorage.getItem("sf_token") ||
    window.localStorage.getItem("token") ||
    window.localStorage.getItem("access_token") ||
    window.localStorage.getItem("auth_token") ||
    window.localStorage.getItem("sflearning_token") ||
    ""
  );
};

const authConfig = () => {
  const token = getStoredToken();
  return token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : {};
};

const emptyStats = {
  kpis: {},
  revenue_by_month: [],
  users_by_month: [],
  enrollments_by_month: [],
  course_health: [],
  low_rated_lessons: [],
};

function normalizeStats(raw = {}) {
  const kpis = raw.kpis || {
    revenue_eur: raw.revenue_eur || 0,
    sales: raw.sales || 0,
    learners: raw.learners || raw.users || 0,
    courses: raw.courses || 0,
    enrollments: raw.enrollments || 0,
    pending_assignments: raw.pending_assignments || 0,
    pending_testimonials: raw.pending_testimonials || 0,
    affiliates: raw.affiliates || 0,
    threads: raw.threads || 0,
    posts: raw.posts || 0,
  };

  return {
    ...emptyStats,
    ...raw,
    kpis,
    revenue_by_month: raw.revenue_by_month || [],
    users_by_month: raw.users_by_month || [],
    enrollments_by_month: raw.enrollments_by_month || [],
    course_health: raw.course_health || [],
    low_rated_lessons: raw.low_rated_lessons || [],
  };
}

function MiniBarChart({ title, data, valueKey = "count", labelKey = "_id", suffix = "" }) {
  const safeData = Array.isArray(data) ? data : [];
  const max = Math.max(1, ...safeData.map((item) => Number(item[valueKey] || 0)));

  return (
    <div className="card p-6">
      <h3 className="font-heading text-xl font-bold mb-4">{title}</h3>
      <div className="space-y-3">
        {safeData.length === 0 && <p className="text-sm text-gray-500">Pas encore assez de données.</p>}
        {safeData.map((item, index) => {
          const value = Number(item[valueKey] || 0);
          const width = Math.max(4, Math.round((value / max) * 100));
          const label = item[labelKey] || `Donnée ${index + 1}`;
          return (
            <div key={`${label}-${index}`}>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>{label}</span>
                <span>{value.toLocaleString("fr-FR")}{suffix}</span>
              </div>
              <div className="h-3 rounded-full bg-emerald-50 overflow-hidden">
                <div className="h-full rounded-full bg-brand-green" style={{ width: `${width}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint }) {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 text-gray-500 mb-1">
        <Icon className="w-4 h-4" />
        <p className="label">{label}</p>
      </div>
      <p className="font-heading text-3xl font-black">{value}</p>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

export default function AdminStats() {
  const [data, setData] = useState(emptyStats);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let alive = true;

    const loadStats = async () => {
      setLoading(true);
      setErrorMessage("");

      try {
        // Route avancée si elle existe côté backend complet.
        const analytics = await api.get("/admin/analytics", authConfig());
        if (alive) setData(normalizeStats(analytics.data));
      } catch (analyticsErr) {
        const analyticsStatus = analyticsErr?.response?.status;

        try {
          // Route stable déjà présente dans backend/server.py.
          const fallback = await api.get("/admin/stats", authConfig());
          if (alive) {
            setData(normalizeStats(fallback.data));
            if (analyticsStatus === 404) {
              setErrorMessage("Statistiques avancées indisponibles pour le moment : affichage des KPIs simples.");
            }
          }
        } catch (statsErr) {
          const status = statsErr?.response?.status || analyticsStatus;
          if (alive) {
            setData(emptyStats);
            if (status === 401) {
              setErrorMessage("Session admin expirée ou token non envoyé. Déconnecte-toi puis reconnecte-toi en admin.");
            } else if (status === 403) {
              setErrorMessage("Accès refusé : ton compte n’a pas les droits admin pour consulter ces statistiques.");
            } else if (status === 404) {
              setErrorMessage("Endpoint statistiques introuvable côté backend. Vérifie que backend/server.py est bien déployé.");
            } else {
              setErrorMessage("Impossible de charger les statistiques pour le moment.");
            }
          }
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    loadStats();
    return () => { alive = false; };
  }, []);

  const revenue = useMemo(
    () => (data?.revenue_by_month || []).map((r) => ({ ...r, revenue: Math.round(Number(r.revenue || 0)) })),
    [data]
  );

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-12">Chargement des statistiques...</div>;

  const k = data?.kpis || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center gap-3 mb-2">
        <BarChart3 className="w-8 h-8 text-brand-green" />
        <p className="label">Pilotage</p>
      </div>
      <h1 className="font-heading text-4xl font-black mb-2">Statistiques avancées</h1>
      <p className="text-gray-600 mb-6">Analyse les ventes, l’engagement, la communauté et les signaux qualité pour améliorer la plateforme.</p>

      {errorMessage && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Kpi icon={Euro} label="Revenu" value={`${Math.round(k.revenue_eur || 0).toLocaleString("fr-FR")}€`} hint={`${k.sales || 0} vente(s)`} />
        <Kpi icon={Users} label="Apprenants" value={k.learners || 0} hint={`${k.enrollments || 0} accès formation`} />
        <Kpi icon={MessageCircle} label="Communauté" value={(k.threads || 0) + (k.posts || 0)} hint={`${k.threads || 0} discussions · ${k.posts || 0} réponses`} />
        <Kpi icon={Star} label="À traiter" value={(k.pending_assignments || 0) + (k.pending_testimonials || 0)} hint="Devoirs + avis en attente" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <MiniBarChart title="Chiffre d’affaires par mois" data={revenue} valueKey="revenue" suffix="€" />
        <MiniBarChart title="Nouveaux apprenants par mois" data={data?.users_by_month || []} />
        <MiniBarChart title="Nouvelles inscriptions formations" data={data?.enrollments_by_month || []} />
        <div className="card p-6">
          <h3 className="font-heading text-xl font-bold mb-4">Formations à surveiller</h3>
          <div className="space-y-3">
            {(data?.course_health || []).length === 0 && <p className="text-sm text-gray-500">Pas encore assez de données.</p>}
            {(data?.course_health || []).map((course) => (
              <div key={course.course_id} className="rounded-2xl border border-emerald-100 p-4">
                <div className="flex justify-between gap-3 mb-2">
                  <p className="font-semibold">{course.title}</p>
                  <p className="text-sm text-gray-500">{course.completion_rate}% complété</p>
                </div>
                <div className="h-2 rounded-full bg-emerald-50 overflow-hidden">
                  <div className="h-full rounded-full bg-brand-green" style={{ width: `${Math.min(100, course.completion_rate || 0)}%` }} />
                </div>
                <p className="text-xs text-gray-500 mt-2">{course.enrolled} inscrit(s) · {course.completed} terminé(s) · {course.lessons_count} contenu(s)</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-5 h-5 text-brand-green" />
          <h3 className="font-heading text-xl font-bold">Leçons les moins bien notées</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b"><th className="py-2">Leçon</th><th>Note</th><th>Avis</th></tr>
            </thead>
            <tbody>
              {(data?.low_rated_lessons || []).map((row) => (
                <tr key={row._id} className="border-b border-gray-100">
                  <td className="py-3 font-medium">{row.lesson?.title || row._id}</td>
                  <td>{Number(row.avg || 0).toFixed(1)}/5</td>
                  <td>{row.count}</td>
                </tr>
              ))}
              {(data?.low_rated_lessons || []).length === 0 && <tr><td colSpan="3" className="py-6 text-center text-gray-500">Aucune note pour le moment.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
